/**
 * Realtime entry point: connects the socket while authenticated and wires server events
 * into the stores. Domain handlers live in sibling files — each feature agent edits only
 * its own file (see apps/web/README.md "Feature ownership"):
 *
 *   chats.ts        chat list, receipts, typing, pins           (foundation / agent 2)
 *   messages.ts     message:new/updated/removed, notifications  (foundation / agent 2)
 *   users.ts        presence, profiles, me, contacts, sessions  (foundation / agent 1)
 *   friends.ts      friends:request / friends:changed (pending requests)
 *   communities.ts  community:*                                 (agent 3)
 *   spaces.ts       space:*, space channel focus / member-list subscriptions
 *   threads.ts      thread:upsert / thread:deleted, threads of changed channels
 *   emojis.ts       space:emojis (custom Space emoji)
 *   stickers.ts     space:stickers (Space stickers)
 *   soundboard.ts   space:sounds, voice:sound (the soundboard)
 *   requests.ts     chat:request, chat:request-removed (message requests)
 *   ignores.ts      ignores:changed (my ignore list)
 *   moderation.ts   space:security (security actions)
 *   guide.ts        space:guide, me:channel-prefs (Server Guide, Browse Channels)
 *   events.ts       space:event* (scheduled events, the "Event starting" reminder)
 *   calls.ts        call:*                                      (agent 4)
 *   status.ts       status:*                                    (agent 4)
 *
 * Every domain exports `register<Domain>Handlers(socket)` and `resync<Domain>(info)`; the
 * resyncs run on every server `ready` (first connect and reconnects), after which the
 * `realtime:ready` bus event fires.
 */
import { bus } from '@/lib/bus';
import { syncPushSubscription } from '@/lib/push';
import { connectSocket, disconnectSocket, onReady, type ReadyInfo } from '@/lib/socket';
import { useAuth } from '@/stores/auth';
import { useChats } from '@/stores/chats';
import { installReadTracking, registerChatHandlers, resyncChats } from './chats';
import { registerCallHandlers, resyncCalls } from './calls';
import { registerCommunityHandlers, resyncCommunities } from './communities';
import { registerEmojiHandlers, resyncEmojis } from './emojis';
import { registerEventHandlers, resyncEvents } from './events';
import { registerFriendHandlers, resyncFriends } from './friends';
import { registerGuideHandlers, resyncGuide } from './guide';
import { registerIgnoresHandlers, resyncIgnores } from './ignores';
import { registerMessageHandlers } from './messages';
import { registerModerationHandlers, resyncModeration } from './moderation';
import { registerRequestsHandlers, resyncRequests } from './requests';
import { registerSoundboardHandlers, resyncSoundboard } from './soundboard';
import { registerSpaceHandlers, resyncSpaces } from './spaces';
import { registerStatusHandlers, resyncStatus } from './status';
import { registerStickerHandlers, resyncSticker } from './stickers';
import { registerThreadHandlers, resyncThreads } from './threads';
import { registerUserHandlers, resyncUsers } from './users';
import { registerVoiceHandlers, resyncVoice } from './voice';
import { registerPreferencesHandlers, resyncPreferences } from './preferences';
import { registerBookmarkHandlers, resyncBookmarks } from './bookmarks';

export { markChatRead } from './chats';
export { handleNewMessage } from './messages';

let running = false;
let cleanups: (() => void)[] = [];

/** The domain resyncs run on every `ready` (exported for tests). */
export async function resyncAll(info: ReadyInfo): Promise<void> {
  // Calls start right away: `call:rejoin` must beat the server's reconnect grace
  // (CALL_RECONNECT_GRACE_MS) and doesn't depend on the chat list. Chats next (they drive
  // the UI), then the rest in parallel.
  await Promise.allSettled([
    resyncCalls(info),
    resyncChats(info).then(() =>
      Promise.allSettled([
        resyncUsers(info),
        resyncFriends(info),
        resyncCommunities(info),
        resyncStatus(info),
        resyncSpaces(info),
        resyncThreads(info),
        resyncEmojis(info),
        resyncSticker(info),
        resyncSoundboard(info),
        resyncRequests(info),
        resyncIgnores(info),
        resyncPreferences(info),
        resyncBookmarks(info),
        resyncModeration(info),
        resyncGuide(info),
        resyncEvents(info),
        resyncVoice(info),
      ]),
    ),
  ]);
}

/** Connect and register all handlers (idempotent). Requires an authenticated session. */
export function startRealtime(): void {
  const token = useAuth.getState().token;
  if (running || !token) return;
  running = true;

  connectSocket(token, (socket) => {
    registerChatHandlers(socket);
    registerMessageHandlers(socket);
    registerUserHandlers(socket);
    registerFriendHandlers(socket);
    registerCommunityHandlers(socket);
    registerSpaceHandlers(socket);
    registerThreadHandlers(socket);
    registerEmojiHandlers(socket);
    registerStickerHandlers(socket);
    registerSoundboardHandlers(socket);
    registerRequestsHandlers(socket);
    registerIgnoresHandlers(socket);
    registerModerationHandlers(socket);
    registerGuideHandlers(socket);
    registerEventHandlers(socket);
    registerVoiceHandlers(socket);
    registerCallHandlers(socket);
    registerStatusHandlers(socket);
    registerPreferencesHandlers(socket);
    registerBookmarkHandlers(socket);
  });

  cleanups.push(
    onReady((info) => {
      void resyncAll(info).finally(() => {
        bus.emit('realtime:ready', {
          userId: info.userId,
          sessionId: info.sessionId,
          reconnect: info.reconnect,
        });
      });
    }),
    installReadTracking(),
  );

  // Don't wait for the socket handshake (or a blocked WebSocket) to show the chat list;
  // every `ready` reloads it again (a fresh request, never this one) to close the gap before
  // rooms were joined.
  void useChats
    .getState()
    .loadChats()
    .catch(() => undefined);
  void syncPushSubscription().catch(() => undefined);
}

/** Disconnect and remove all handlers (idempotent). */
export function stopRealtime(): void {
  if (!running) return;
  running = false;
  for (const fn of cleanups) fn();
  cleanups = [];
  disconnectSocket();
}

/**
 * Start realtime whenever the auth store is authenticated, stop it otherwise.
 * Called once from main.tsx. Returns an unsubscribe function.
 */
export function bindRealtimeToAuth(): () => void {
  const apply = () => {
    const { status } = useAuth.getState();
    if (status === 'authenticated') startRealtime();
    else stopRealtime();
  };
  apply();
  return useAuth.subscribe((s, prev) => {
    if (s.token !== prev.token && prev.token) stopRealtime();
    if (s.status !== prev.status || s.token !== prev.token) apply();
  });
}
